import { useMemo } from 'react';
import { FieldValues, Path, UseFormReturn } from 'react-hook-form';

import { AssetAllocation } from '@ndvr/portfolio-api';

import { AssetInfo } from '@/cip/_openapi/portfolio-optimizer';
import { useAssetsByIds } from '@/hooks/market-data-queries';

import { extractSecurityIdentifiers } from './strategy-utils';
import { useAssetAllocationManager } from './useAssetAllocationManager';

/**
 * Wires a form's `assetAllocations` field up to everything StrategyAssetTable (directly, or via
 * StrategyFormBody) needs: add/remove/replacement handlers, the replacement-popover state, and
 * the ticker → AssetInfo lookup for rendering. Every place that edits a strategy's asset list
 * (NewStrategyPage, EditStrategyDialog, the inline NewStrategyBuilderPanel) re-derived this same
 * bundle independently — this hook is the one place it's wired up.
 */
export function useAssetTableProps<T extends FieldValues & { assetAllocations: AssetAllocation[] }>(
    form: UseFormReturn<T>,
) {
    const assetAllocations = form.watch('assetAllocations' as Path<T>) as AssetAllocation[];

    const {
        showAddAsset,
        setShowAddAsset,
        addingReplacementForAsset,
        setAddingReplacementForAsset,
        handleAddAsset,
        removeAsset,
        handleUpdateWeight,
        handleAddReplacement,
        removeReplacement,
        evenSplit,
    } = useAssetAllocationManager({ form, assetAllocations });

    const securityIdentifiers = useMemo(() => extractSecurityIdentifiers(assetAllocations), [assetAllocations]);
    const { data: assets = [] } = useAssetsByIds(securityIdentifiers);
    const getAssetInfo = (securityId: string): AssetInfo | undefined => assets.find((a) => a.sId === securityId);

    return {
        assetAllocations,
        getAssetInfo,
        showAddAsset,
        onShowAddAsset: () => setShowAddAsset(true),
        onCancelAdd: () => setShowAddAsset(false),
        onAddAsset: handleAddAsset,
        onUpdateWeight: handleUpdateWeight,
        onRemoveAsset: removeAsset,
        onAddReplacement: handleAddReplacement,
        onRemoveReplacement: removeReplacement,
        addingReplacementForAsset,
        setAddingReplacementForAsset,
        onEvenSplit: evenSplit,
    };
}
